import { getConfig } from "@/config";
import { emitter } from "@/utils/emitter/mitt";

export interface WebAudioItem {
  id: string;
  text: string;
  audioUrl: string;
  duration?: number;
  voiceNo?: number;
  productId?: string;
  storeIndex?: number;
  commentId?: string;
}

export class SeamlessWebAudioPlayer {
  private ac: AudioContext;
  private gain: GainNode;
  private freezeGuard?: GainNode;
  private analyser?: AnalyserNode;
  private anaBuf?: Float32Array<ArrayBuffer>;
  private comp?: DynamicsCompressorNode;
  private dest?: MediaStreamAudioDestinationNode;
  private attachedAudio?: HTMLAudioElement | null;
  private playlist: (WebAudioItem & { buffer?: AudioBuffer })[] = [];
  private currentIndex = 0;
  private isPlaying = false;
  private nextStartAt = 0;
  private overlap = 0.2; // 交叉淡入淡出（单位秒）
  private fadeMs = 180; // 渐变时长（单位毫秒）
  private foreHandoff = false; // 可见态前台交接：true 则 onended 不再安排下一条
  private suppressAutoNext = false; // 挂起切换：禁止 onended 自动安排下一条（前/后台均有效）
  private allowedProductId: string | null = null; // 只允许调度的产品ID（苹果端切换专用）
  private onProgress?: (index: number, progress: number) => void;
  private onComplete?: (index: number) => void;
  private progressTimer?: number;
  private activeSources = new Map<number, AudioBufferSourceNode>();
  private sourceGains = new Map<number, GainNode>();
  private destroyed = false;
  private tearingDown = false; // 结束/销毁过程中，屏蔽 onended 的推进与保活
  private roomId: string;
  private keepAliveSrc?: AudioBufferSourceNode;
  private keepAliveGain?: GainNode;
  private keepAliveActive = false;
  // 目标句间间隙（秒）：用于精确控制“可听见的静默间隔”。默认 0（不干预）。
  private targetGapSec = 0;
  // 渐入起点的最低音量，避免一开始过低导致主观“静音”感
  // private minStartGain = 0.02;
  // 是否强制上一条的“可听部分”完全结束后再启动下一条
  private ensurePrevComplete = false;
  // 可听结束后的最小保护间隔（秒）
  private audibleEndGuardSec = 0.02;
  private prevFadeDelaySec = 0.015; // 上一条淡出延迟
  private minStartGain = 0.08; // 渐入起点最低增益

  // 解码并发限制
  private maxDecodeConcurrency = 3;
  private decodeInFlight = 0;
  private decodeQueue: {
    url: string;
    resolve: (ab: AudioBuffer) => void;
    reject: (e: any) => void;
    priority: number;
    tag?: string;
  }[] = [];
  private invalidTags: Set<string> = new Set();

  constructor(roomId?: string) {
    const AC: any =
      (window as any).AudioContext || (window as any).webkitAudioContext;
    this.ac = new AC();
    this.gain = this.ac.createGain();
    try {
      this.freezeGuard = this.ac.createGain();
      (this.freezeGuard as GainNode).gain.value = 1.0;
      // 动态压缩器（默认温和设置），切换瞬间临时加深，窗口后还原
      this.comp = this.ac.createDynamicsCompressor();
      try {
        (this.comp as DynamicsCompressorNode).threshold.value = -4;
        (this.comp as DynamicsCompressorNode).ratio.value = 1.5;
        (this.comp as DynamicsCompressorNode).knee.value = 8;
        (this.comp as DynamicsCompressorNode).attack.value = 0.005;
        (this.comp as DynamicsCompressorNode).release.value = 0.08;
      } catch {}
      // 在 freezeGuard 前串一个 Analyser，用于自适应脉冲深度
      this.analyser = this.ac.createAnalyser();
      this.analyser.fftSize = 256;
      this.anaBuf = new Float32Array(this.analyser.fftSize);
      this.gain.connect(this.comp);
      this.comp.connect(this.analyser);
      this.analyser.connect(this.freezeGuard);
      (this.freezeGuard as GainNode).connect(this.ac.destination);
    } catch {
      // 退化：没有 freezeGuard 时直接连到 destination
      this.gain.connect(this.ac.destination);
    }
    this.roomId = roomId || "unknown";

    // 根据配置调整重叠时间
    try {
      const cfg: any = getConfig && getConfig();
      const ov = Number(cfg?.Audio?.OverlapSeconds ?? 0.2);
      if (!Number.isNaN(ov)) this.overlap = Math.max(0, Math.min(0.5, ov));
      const gapMs = Number(cfg?.Audio?.TargetGapMs ?? 0);
      if (!Number.isNaN(gapMs))
        this.targetGapSec = Math.max(0, Math.min(1.0, gapMs / 1000));
      const strict = Boolean(cfg?.Audio?.EnsurePrevComplete ?? false);
      this.ensurePrevComplete = strict;
      const guardMs = Number(cfg?.Audio?.AudibleEndGuardMs ?? 20);
      if (!Number.isNaN(guardMs))
        this.audibleEndGuardSec = Math.max(0, Math.min(0.2, guardMs / 1000));
      const msg = Number(cfg?.Audio?.MinStartGain ?? 0.08);
      if (!Number.isNaN(msg))
        this.minStartGain = Math.max(0.0, Math.min(0.5, msg));
      const pfd = Number(cfg?.Audio?.PrevFadeDelayMs ?? 15);
      if (!Number.isNaN(pfd))
        this.prevFadeDelaySec = Math.max(0, Math.min(0.05, pfd / 1000));
    } catch {}
  }

  // 运行时调整重叠与淡入时长（隐藏态禁止重叠时使用）
  setOverlap(seconds: number) {
    this.overlap = Math.max(0, Math.min(0.5, Number(seconds) || 0));
  }

  setFadeMs(ms: number) {
    this.fadeMs = Math.max(0, Math.min(1000, Math.floor(Number(ms) || 0)));
  }

  // 设置“可听见的目标间隙”（毫秒）。播放器将基于每条音频的尾部静音量进行补偿，
  // 使相邻两条语音之间的静默尽量接近该数值（±10~20ms）。
  setTargetGapMs(ms: number) {
    const v = Math.floor(Number(ms) || 0);
    this.targetGapSec = Math.max(0, Math.min(1.0, v / 1000));
  }

  // 设置渐入起点的最低音量（0.0~0.5），避免起点过低造成感知静默
  setMinStartGain(gain: number) {
    const g = Number(gain);
    if (!Number.isFinite(g)) return;
    this.minStartGain = Math.max(0.0, Math.min(0.5, g));
  }

  // 启用/关闭“上一条可听内容必须播放完毕”边界约束（可选保护间隔，默认20ms）
  setEnsurePrevComplete(flag: boolean, guardMs: number = 20) {
    this.ensurePrevComplete = !!flag;
    this.audibleEndGuardSec = Math.max(
      0,
      Math.min(0.2, (Number(guardMs) || 0) / 1000)
    );
  }

  setPrevFadeDelayMs(ms: number) {
    const v = Math.floor(Number(ms) || 0);
    this.prevFadeDelaySec = Math.max(0, Math.min(0.05, v / 1000));
  }

  // 设置前台交接标志（可见态避免 WA 安排下一句）
  setForeHandoff(flag: boolean) {
    this.foreHandoff = !!flag;
    try {
      console.warn(`[wa-player] foreHandoff=${this.foreHandoff}`);
    } catch {}
  }

  // 挂起切换专用：无论可见/隐藏，onended 不再安排下一条
  setSuppressAutoNext(flag: boolean) {
    this.suppressAutoNext = !!flag;
    try {
      console.warn(`[wa-player] suppressAutoNext=${this.suppressAutoNext}`);
    } catch {}
  }

  // 仅调度与此产品ID匹配的条目；传 null/空则不过滤
  setAllowedProductId(pid?: string) {
    this.allowedProductId = pid ? String(pid) : null;
    try {
      console.warn(
        `[wa-player] allowedProductId=${this.allowedProductId ?? "ANY"}`
      );
    } catch {}
  }

  // 清理 currentIndex 之后所有“非允许产品”的待播条目；可选停止已预排的 source
  purgeQueuedExceptAllowed(stopPreScheduled: boolean = true) {
    const allowed = this.allowedProductId;
    if (!allowed) return;
    const start = Math.max(0, this.currentIndex + 1);
    // 从末尾向前删除，避免索引移动
    for (let i = this.playlist.length - 1; i >= start; i--) {
      const it: any = this.playlist[i];
      const pid = it?.productId || null;
      if (pid !== allowed) {
        try {
          console.warn(`[wa-player] purge queued index=${i} pid=${pid}`);
        } catch {}
        // 停止已预排的 source（若存在）
        if (stopPreScheduled) {
          const src = this.activeSources.get(i);
          const g = this.sourceGains.get(i);
          if (src) {
            try {
              const now = this.ac.currentTime;
              if (g && g.gain) {
                try {
                  g.gain.cancelScheduledValues(now);
                } catch {}
                try {
                  g.gain.setValueAtTime(g.gain.value, now);
                } catch {}
                try {
                  g.gain.linearRampToValueAtTime(0.0001, now + 0.06);
                } catch {
                  g.gain.value = 0.0001;
                }
              }
              src.stop(now + 0.08);
            } catch {}
            try {
              this.activeSources.delete(i);
            } catch {}
            try {
              this.sourceGains.delete(i);
            } catch {}
          }
        }
        this.playlist.splice(i, 1);
      }
    }
  }

  async unlock() {
    if (this.ac.state !== "running") {
      try {
        await this.ac.resume();
      } catch {}
    }
  }

  setProgressCallback(cb: (index: number, progress: number) => void) {
    this.onProgress = cb;
  }

  setCompleteCallback(cb: (index: number) => void) {
    this.onComplete = cb;
  }

  setVolume(volume: number) {
    try {
      this.gain.gain.value = volume;
    } catch {}
  }

  setMuted(muted: boolean) {
    try {
      const target = muted ? 0 : 1;
      const now = this.ac.currentTime;
      // 平滑包络，避免瞬时点击声
      const cur = (() => {
        try {
          return this.gain.gain.value;
        } catch {
          return target;
        }
      })();
      try {
        this.gain.gain.cancelScheduledValues(now);
      } catch {}
      try {
        this.gain.gain.setValueAtTime(cur, now);
      } catch {}
      try {
        this.gain.gain.linearRampToValueAtTime(target, now + 0.04);
      } catch {
        this.gain.gain.value = target;
      }
    } catch {
      this.setVolume(muted ? 0 : 1);
    }
  }

  getStatus() {
    const cur = this.playlist[this.currentIndex];
    const dur = cur?.duration || cur?.buffer?.duration || 0;
    const now = this.ac.currentTime;
    // 以“有效时长”计算当前曲目的开始时间；若尚未调度（nextStartAt 未设置），视作 currentTime=0，避免误判“接近结束”
    const eff = cur?.duration || cur?.buffer?.duration || 0;
    let t = 0;
    if (eff > 0 && this.nextStartAt && this.nextStartAt > 0) {
      const startAt = this.nextStartAt - eff;
      t = Math.max(0, now - startAt);
      // 若尚未真正到达 startAt（负偏差），进度按 0 处理，避免切换瞬间抖动
      if (now < startAt) t = 0;
      // 若 nextStartAt 异常落后（极端），将 t 限制在 [0, dur]
      if (t > dur && dur > 0) t = Math.min(t, dur);
    } else {
      t = 0;
    }
    return {
      isPlaying: this.isPlaying,
      currentIndex: this.currentIndex,
      totalItems: this.playlist.length,
      currentItem: cur || null,
      currentTime: t,
      duration: dur,
      muted: this.gain.gain.value === 0,
      roomId: this.roomId,
      // 暴露当前活跃的 source 数量，用于可见性/边界严格判定
      activeSourcesCount: (() => {
        try {
          return this.activeSources.size;
        } catch {
          return 0;
        }
      })()
    };
  }

  // 对齐当前播放索引（用于 HTML5→WebAudio 隐藏态交接时避免重复）
  setCurrentIndex(index: number) {
    const clamped = Math.max(
      0,
      Math.min(this.playlist.length - 1, Math.floor(Number(index) || 0))
    );
    this.currentIndex = clamped;
    // 重置计划时间，下一次 play() 将按当前时间调度
    this.nextStartAt = this.ac.currentTime;
  }

  async addToPlaylist(item: WebAudioItem) {
    // 追加条目并异步解码（带重试）
    const entry = { ...item } as WebAudioItem & {
      buffer?: AudioBuffer;
      __retryCount?: number;
    };
    this.playlist.push(entry);
    try {
      console.warn(
        `[probe] WA_ADD playlist item=${entry.id} total=${this.playlist.length}`
      );
    } catch {}
    this.decodeWithRetry(entry);
  }

  // 带优先级/标签的加入：用于目标首条插队
  async addToPlaylistWithPriority(
    item: WebAudioItem,
    priority: number,
    tag?: string
  ) {
    const entry = { ...item } as WebAudioItem & {
      buffer?: AudioBuffer;
      __retryCount?: number;
    };
    this.playlist.push(entry);
    try {
      console.warn(
        `[probe] WA_ADD item=${entry.id} total=${this.playlist.length}`
      );
    } catch {}
    this.decodeWithRetry(entry, 0, priority, tag);
  }

  private decodeWithRetry(
    entry: WebAudioItem & { buffer?: AudioBuffer; __retryCount?: number },
    attempt: number = 0,
    priority?: number,
    tag?: string
  ) {
    const MAX_RETRY = 5;
    const baseDelay = 400;
    const nextAttempt = attempt + 1;
    const delay = Math.min(baseDelay * Math.pow(2, attempt), 4000);

    const doDecode = () => {
      const decodePromise =
        typeof priority === "number"
          ? this.enqueueDecode(entry.audioUrl, priority, tag)
          : this.enqueueDecode(entry.audioUrl);

      decodePromise
        .then(ab => {
          entry.__retryCount = 0;
          this.onDecodeSuccess(entry, ab);
        })
        .catch(err => {
          entry.__retryCount = (entry.__retryCount ?? 0) + 1;
          console.warn(
            `🍎 WebAudio: 解码失败(${entry.__retryCount}/${MAX_RETRY}) id=${entry.id}`,
            err
          );
          if (entry.__retryCount < MAX_RETRY) {
            setTimeout(
              () => this.decodeWithRetry(entry, nextAttempt, priority, tag),
              delay
            );
          } else {
            this.onDecodeExhaust(entry);
          }
        });
    };

    if (attempt === 0) {
      doDecode();
    } else {
      setTimeout(doDecode, delay);
    }
  }

  private onDecodeSuccess(
    entry: WebAudioItem & { buffer?: AudioBuffer },
    ab: AudioBuffer
  ) {
    const s = this.analyzeSilence(ab);
    entry.buffer = ab;
    entry.duration = s.effectiveSec;
    (entry as any).__tailSilenceSec = s.tailSilenceSec;
    (entry as any).__headSilenceSec = s.headSilenceSec;
    if (s.effEndZeroCrossSec && isFinite(s.effEndZeroCrossSec)) {
      (entry as any).__effEndZeroCrossSec = s.effEndZeroCrossSec;
    }
    console.log(
      `🍎 WebAudio: 解码完成, raw=${ab.duration.toFixed(2)}s, eff=${s.effectiveSec.toFixed(2)}s, head=${s.headSilenceSec.toFixed(3)}s, tail=${s.tailSilenceSec.toFixed(3)}s`
    );

    // 如果正在运行 keepAlive,说明旧音频已经念完了现在有新音频了
    if (this.keepAliveActive) {
      console.warn(`[fix] 检测到 keepAlive 活跃,停止并启动真实播放`);
      this.stopKeepAlive();
      this.isPlaying = true;
      this.nextStartAt = Math.max(
        this.ac.currentTime + 0.02,
        this.ac.currentTime
      );
      // 保持 currentIndex 为 0，从列表开头开始播放
      if (this.playlist.length === 1) {
        this.currentIndex = 0;
      }
      this.currentIndex = Math.min(this.currentIndex, this.playlist.length - 1);
      this.scheduleIndex(this.currentIndex);
      this.startProgressTimer();
      return;
    }

    //  增强自动播放判断
    // 不仅检查 !isPlaying,还要检查是否真的有音频在播放
    const hasActiveSource = this.activeSources.has(this.currentIndex);
    const shouldAutoPlay =
      !this.foreHandoff &&
      this.playlist.length > 0 &&
      (!this.isPlaying || !hasActiveSource);

    if (shouldAutoPlay) {
      // 从 currentIndex 开始检查，找到第一个已解码的音频进行播放
      let playIndex = this.currentIndex;
      while (playIndex < this.playlist.length) {
        const item = this.playlist[playIndex];
        if (item && item.buffer) {
          break;
        }
        playIndex++;
      }
      // 确保 playIndex 不越界
      playIndex = Math.min(playIndex, this.playlist.length - 1);

      console.warn(
        `[fix] 自动启动播放: idx=${playIndex}, hasActiveSource=${hasActiveSource}`
      );
      this.isPlaying = true;
      this.currentIndex = playIndex;
      this.nextStartAt = Math.max(
        this.ac.currentTime + 0.02,
        this.ac.currentTime
      );
      this.scheduleIndex(this.currentIndex);
      this.startProgressTimer();
      return;
    }

    if (
      !this.foreHandoff &&
      this.isPlaying &&
      this.currentIndex + 1 < this.playlist.length
    ) {
      const cur = this.playlist[this.currentIndex];
      if (cur?.buffer) {
        const effectiveCurDur =
          cur.duration && cur.duration > 0 ? cur.duration : cur.buffer.duration;
        const curEnd = this.nextStartAt;
        const now = this.ac.currentTime;
        const startedAt = curEnd - effectiveCurDur;
        const elapsed = Math.max(0, now - startedAt);
        const remaining = Math.max(0, effectiveCurDur - elapsed);
        const threshold = Math.max(this.overlap * 2, 0.7);
        const MIN_PREFETCH_TIME = 0.1; // 至少提前100ms预取
        if (remaining <= threshold && remaining >= MIN_PREFETCH_TIME) {
          try {
            console.warn(
              `[probe] WA_SCHEDULE_REASON=PREFETCH index=${this.currentIndex + 1} remaining=${remaining.toFixed(3)}s`
            );
          } catch {}
          this.scheduleIndex(this.currentIndex + 1);
        } else if (remaining < MIN_PREFETCH_TIME && remaining > 0) {
          // 如果剩余时间太短，延迟到ONENDED处理，避免竞态
          try {
            console.warn(
              `[probe] WA_PREFETCH_DEFERRED index=${this.currentIndex + 1} remaining=${remaining.toFixed(3)}s (too close to end)`
            );
          } catch {}
        }
      }
    }
  }

  private onDecodeExhaust(entry: WebAudioItem & { buffer?: AudioBuffer }) {
    console.error(`🍎 WebAudio: 多次解码失败，放弃条目 id=${entry.id}`);
    const idx = this.playlist.indexOf(entry);
    if (idx >= 0) {
      const removingCurrent = idx === this.currentIndex;
      this.playlist.splice(idx, 1);
      if (removingCurrent) {
        if (this.currentIndex >= this.playlist.length) {
          this.currentIndex = Math.max(0, this.playlist.length - 1);
        }
        this.isPlaying = false;
        try {
          this.onComplete && this.onComplete(this.currentIndex);
        } catch {}
      }
    }
  }

  // 将紧急项安排为“下一条”（当前曲目结束后首先播放）
  async addUrgentNext(item: WebAudioItem) {
    // 插入到 currentIndex 之后的位置，成为下一条
    const entry = { ...item } as WebAudioItem & { buffer?: AudioBuffer };
    const insertPos = Math.min(
      this.playlist.length,
      Math.max(0, this.currentIndex + 1)
    );
    this.playlist.splice(insertPos, 0, entry);
    try {
      const ab = await this.enqueueDecode(entry.audioUrl);
      const s = this.analyzeSilence(ab);
      entry.buffer = ab;
      entry.duration = s.effectiveSec;
      (entry as any).__tailSilenceSec = s.tailSilenceSec;
      (entry as any).__headSilenceSec = s.headSilenceSec;
      if (s.effEndZeroCrossSec && isFinite(s.effEndZeroCrossSec)) {
        (entry as any).__effEndZeroCrossSec = s.effEndZeroCrossSec;
      }
      // 若当前未在播，则立即安排播放（作为下一条会在 onended 自动接力）
      if (!this.isPlaying) {
        this.isPlaying = true;
        this.nextStartAt = Math.max(
          this.ac.currentTime + 0.02,
          this.ac.currentTime
        );
        this.scheduleIndex(this.currentIndex);
        this.startProgressTimer();
      }
    } catch {
      // 解码失败则移除该条
      try {
        const idx = this.playlist.indexOf(entry);
        if (idx >= 0) this.playlist.splice(idx, 1);
      } catch {}
    }
  }

  // 将紧急项以 FIFO 方式插入到“当前之后的紧急段”末尾
  async addUrgentFIFO(item: WebAudioItem) {
    const entry = { ...item } as WebAudioItem & { buffer?: AudioBuffer };
    // 定位紧急段末尾
    let insertPos = Math.min(
      this.playlist.length,
      Math.max(0, this.currentIndex + 1)
    );
    while (
      insertPos < this.playlist.length &&
      (this.playlist[insertPos] as any)?.voiceNo === -1
    )
      insertPos++;
    this.playlist.splice(insertPos, 0, entry);
    try {
      // 防抑：在紧急解码完成前，暂时关闭自动衔接普通下一条，避免被顶掉
      this.setSuppressAutoNext(true);
      const ab = await this.enqueueDecode(entry.audioUrl);
      const s = this.analyzeSilence(ab);
      entry.buffer = ab;
      entry.duration = s.effectiveSec;
      (entry as any).__tailSilenceSec = s.tailSilenceSec;
      (entry as any).__headSilenceSec = s.headSilenceSec;
      if (s.effEndZeroCrossSec && isFinite(s.effEndZeroCrossSec)) {
        (entry as any).__effEndZeroCrossSec = s.effEndZeroCrossSec;
      }
      // 解码就绪，恢复自动推进
      this.setSuppressAutoNext(false);
      if (!this.isPlaying) {
        this.isPlaying = true;
        this.nextStartAt = Math.max(
          this.ac.currentTime + 0.02,
          this.ac.currentTime
        );
        this.scheduleIndex(this.currentIndex);
        this.startProgressTimer();
      }
    } catch {
      try {
        const idx = this.playlist.indexOf(entry);
        if (idx >= 0) this.playlist.splice(idx, 1);
      } catch {}
      try {
        this.setSuppressAutoNext(false);
      } catch {}
    }
  }

  // 排队控制的解码入口：限制最大并发，避免带宽/CPU峰值
  private enqueueDecode(
    url: string,
    priority: number = 0,
    tag?: string
  ): Promise<AudioBuffer> {
    return new Promise<AudioBuffer>((resolve, reject) => {
      const task = { url, resolve, reject, priority, tag };
      const tryStartNext = () => {
        if (this.decodeInFlight >= this.maxDecodeConcurrency) return false;
        if (this.decodeQueue.length === 0) return false;
        // 取优先级最高的任务
        let bestIdx = 0;
        let bestPr = this.decodeQueue[0].priority;
        for (let i = 1; i < this.decodeQueue.length; i++) {
          if (this.decodeQueue[i].priority > bestPr) {
            bestPr = this.decodeQueue[i].priority;
            bestIdx = i;
          }
        }
        const next = this.decodeQueue.splice(bestIdx, 1)[0];
        // 跳过已失效标签
        if (next.tag && this.invalidTags.has(next.tag)) {
          setTimeout(() => {
            tryStartNext();
          }, 0);
          return false;
        }
        this.decodeInFlight++;
        this.fetchAndDecode(next.url)
          .then(ab => next.resolve(ab))
          .catch(e => next.reject(e))
          .finally(() => {
            this.decodeInFlight = Math.max(0, this.decodeInFlight - 1);
            setTimeout(() => {
              tryStartNext();
            }, 0);
          });
        return true;
      };
      this.decodeQueue.push(task);
      tryStartNext();
    });
  }

  // 使某标签的排队任务失效（用于连续切换丢弃旧目标）
  invalidateQueuedByTag(tag: string) {
    try {
      this.invalidTags.add(tag);
    } catch {}
    try {
      this.decodeQueue = this.decodeQueue.filter(t => t.tag !== tag);
    } catch {}
  }

  // 取消预排/活动的指定索引（用于替换未来项时撤销原预排）
  private unscheduleIndex(index: number) {
    try {
      const src = this.activeSources.get(index);
      const g = this.sourceGains.get(index);
      if (src) {
        const now = this.ac.currentTime;
        try {
          g && (g as any).gain && (g as any).gain.cancelScheduledValues(now);
        } catch {}
        try {
          g &&
            (g as any).gain &&
            (g as any).gain.setValueAtTime((g as any).gain.value, now);
        } catch {}
        try {
          g &&
            (g as any).gain &&
            (g as any).gain.linearRampToValueAtTime(0.0001, now + 0.06);
        } catch {}
        try {
          src.stop(now + 0.08);
        } catch {}
      }
      if (src) this.activeSources.delete(index);
      if (g) this.sourceGains.delete(index);
    } catch {}
  }

  // 解码成功后再替换未来项（避免长时间“解码中”导致空档）
  async replaceFutureItemById(
    id: string,
    newText?: string,
    newUrl?: string
  ): Promise<boolean> {
    const idx = this.playlist.findIndex((it: any) => it?.id === id);
    if (idx < 0) return false;
    if (idx <= this.currentIndex) {
      // 正在播放或已播放：仅更新文本元数据，音频不强替换
      if (typeof newText === "string") {
        try {
          (this.playlist[idx] as any).text = newText;
        } catch {}
      }
      return false;
    }
    const it = this.playlist[idx] as any;
    let ab: AudioBuffer | null = null;
    if (newUrl && newUrl !== it.audioUrl) {
      try {
        ab = await this.fetchAndDecode(newUrl);
      } catch {
        return false;
      }
    }
    // 替换前撤销已预排
    this.unscheduleIndex(idx);
    if (typeof newText === "string") {
      try {
        it.text = newText;
      } catch {}
    }
    if (ab && newUrl) {
      const s = this.analyzeSilence(ab);
      it.audioUrl = newUrl;
      it.buffer = ab;
      it.duration = s.effectiveSec;
      it.__tailSilenceSec = s.tailSilenceSec;
      it.__headSilenceSec = s.headSilenceSec;
      if (s.effEndZeroCrossSec && isFinite(s.effEndZeroCrossSec))
        it.__effEndZeroCrossSec = s.effEndZeroCrossSec;
    }
    return true;
  }

  async play() {
    this.tearingDown = false;
    await this.unlock();

    console.warn(
      `[probe] WA_PLAY_CALL isPlaying=${this.isPlaying} keepAlive=${this.keepAliveActive} curIdx=${this.currentIndex}`
    );

    // 🔧 关键修复: 如果 keepAlive 活跃,先停止它
    if (this.keepAliveActive) {
      console.warn(`[fix] play() 停止 keepAlive,启动真实播放`);
      this.stopKeepAlive();
    }
    try {
      console.warn(
        `[probe] WA_PLAY_CALL isPlaying=${this.isPlaying} curIdx=${this.currentIndex}`
      );
    } catch {}
    // 🔧 改进: 即使 isPlaying=true,如果没有真实的活跃source,也应该启动播放
    const hasActiveSource = this.activeSources.has(this.currentIndex);

    if (this.isPlaying && hasActiveSource) {
      console.warn(`[fix] play() 已在播放且有活跃source,跳过`);
      return;
    }

    if (this.currentIndex >= this.playlist.length) {
      console.warn(
        `[fix] play() 索引越界: ${this.currentIndex} >= ${this.playlist.length}`
      );
      return;
    }

    const cur = this.playlist[this.currentIndex];
    if (!cur?.buffer) {
      console.warn(`[fix] play() 当前项无buffer,等待解码`);
      return;
    }

    this.isPlaying = true;
    this.nextStartAt = Math.max(
      this.ac.currentTime + 0.02,
      this.ac.currentTime
    );

    console.warn(`[probe] WA_SCHEDULE_REASON=PLAY index=${this.currentIndex}`);

    this.scheduleIndex(this.currentIndex);
    this.startProgressTimer();
  }

  pause() {
    // WebAudio 无法暂停单个 source，只能停止。这里选择将主输出静音作为“暂停”。
    this.isPlaying = false;
    this.setMuted(true);
    this.stopProgressTimer();
  }

  resume() {
    this.tearingDown = false;
    this.setMuted(false);
    this.isPlaying = true;
    this.startProgressTimer();
  }

  stop() {
    // 进入拆除保护，避免 onended 在停止过程中推进与重启保活
    this.tearingDown = true;
    this.isPlaying = false;
    try {
      const keys = Array.from(this.activeSources.keys());
      console.log(
        `[wa-player] stop room=${this.roomId} activeSources=[${keys.join(",")}] playlist=${this.playlist.length} idx=${this.currentIndex}`
      );
    } catch {}
    this.stopProgressTimer();
    try {
      // 主输出做一次短淡出，避免停止瞬间点击声
      const now = this.ac.currentTime;
      const FADE = 0.06; // 60ms
      try {
        this.gain.gain.cancelScheduledValues(now);
      } catch {}
      try {
        this.gain.gain.setValueAtTime(this.gain.gain.value, now);
      } catch {}
      try {
        this.gain.gain.linearRampToValueAtTime(0.0, now + FADE);
      } catch {
        this.gain.gain.value = 0.0;
      }
      setTimeout(
        () => {
          for (const [, src] of this.activeSources) {
            try {
              src.onended = null as any;
            } catch {}
            try {
              src.stop();
            } catch {}
          }
          this.activeSources.clear();
          this.sourceGains.clear();
        },
        Math.round((FADE + 0.02) * 1000)
      );
    } catch {
      for (const [, src] of this.activeSources) {
        try {
          src.stop();
        } catch {}
      }
      this.activeSources.clear();
      this.sourceGains.clear();
    }
    this.playlist = [];
    this.currentIndex = 0;
    this.nextStartAt = this.ac.currentTime;
    this.stopKeepAlive();
  }

  destroy() {
    if (this.destroyed) return;
    try {
      console.log(`[wa-player] destroy room=${this.roomId}`);
    } catch {}
    this.stop();
    try {
      this.ac.close();
    } catch {}
    this.destroyed = true;
  }

  // 查询某条目是否已完成解码
  hasDecoded(id: string): boolean {
    try {
      const idx = this.playlist.findIndex((it: any) => it?.id === id);
      if (idx < 0) return false;
      return !!(this.playlist[idx] as any)?.buffer;
    } catch {
      return false;
    }
  }

  // 等待条目完成解码（带超时）
  async waitUntilDecoded(
    id: string,
    timeoutMs: number = 8000,
    intervalMs: number = 60
  ): Promise<boolean> {
    const start = Date.now();
    if (this.hasDecoded(id)) return true;
    return new Promise(resolve => {
      const tick = () => {
        if (this.hasDecoded(id)) {
          resolve(true);
          return;
        }
        if (Date.now() - start >= timeoutMs) {
          resolve(false);
          return;
        }
        setTimeout(tick, Math.max(16, intervalMs));
      };
      setTimeout(tick, Math.max(16, intervalMs));
    });
  }

  private scheduleIndex(index: number) {
    if (index < 0 || index >= this.playlist.length) return;
    const it = this.playlist[index];
    if (!it?.buffer) return;

    // 🔧 幂等保护：同一索引只允许存在一个活跃 source，避免重复调度造成多次 audioStart
    const hasActiveSource = this.activeSources.has(index);

    if (hasActiveSource) {
      console.warn(
        `[fix] WA_SKIP_DUP_SCHEDULE index=${index} (已有活跃source)`
      );

      // 关键修复：若该条是“预排后转正”(index 变为 currentIndex)，补发一次 audioStart
      // 避免因为命中幂等 return 而漏报开始事件
      if (index === this.currentIndex) {
        const activeSrc = this.activeSources.get(index) as any;
        if (activeSrc && !activeSrc.__audioStartEmitted) {
          activeSrc.__audioStartEmitted = true;
          emitter.emit("audioStart", {
            roomId: this.roomId,
            audioId: it.id,
            text: it.text,
            commentId: it.commentId || null,
            player: "seamlessWebAudio"
          });
        }
      }

      // 当前索引被重复触发时，尽量维持 nextStartAt 的单调性
      if (index === this.currentIndex && it.duration) {
        const safeDur = Math.max(
          0.02,
          it.duration + Math.max(0, this.targetGapSec)
        );
        const currentStart = this.nextStartAt - safeDur;
        this.nextStartAt = Math.max(
          currentStart + safeDur,
          this.ac.currentTime
        );
      }
      return;
    }
    // 当前索引且无活跃 source：可能是断播恢复，允许继续调度
    if (index === this.currentIndex) {
      console.warn(
        `[fix] WA_RESUME_PLAY index=${index} (检测到无活跃source,恢复播放)`
      );
    }
    try {
      const dup = this.activeSources.has(index);
      console.warn(
        `[probe] WA_SCHEDULE index=${index} dup=${dup} curIdx=${this.currentIndex} playing=${this.isPlaying}`
      );
    } catch {}

    const src = this.ac.createBufferSource();
    src.buffer = it.buffer!;
    const g = this.ac.createGain();
    // 从 0 起始，交由包络平滑淡入，避免切换瞬间的点击声
    g.gain.value = 0.0;
    src.connect(g);
    g.connect(this.gain);

    // 统一使用不早于当前时间的起点，避免过去时间导致并发异常
    // 稍加提前量，给包络与调度留出时间，降低硬起步
    const safeNow = this.ac.currentTime + 0.015;
    let startAt =
      index === this.currentIndex
        ? Math.max(this.nextStartAt, safeNow)
        : Math.max(this.nextStartAt - this.overlap, safeNow);
    // 边界保护：若启用 ensurePrevComplete，则下一条不得早于“上一条可听结束 + 保护间隔”
    if (index > this.currentIndex && this.ensurePrevComplete) {
      const audibleEndAt = this.nextStartAt - Math.max(0, this.targetGapSec);
      const minStart = audibleEndAt + this.audibleEndGuardSec;
      startAt = Math.max(startAt, minStart);
    }
    // 若存在头部静音，则从 offset 位置开始播放，去除起始空白
    const headSkip = Math.max(0, Number((it as any).__headSilenceSec) || 0);
    try {
      headSkip > 0 ? src.start(startAt, headSkip) : src.start(startAt);
    } catch {}

    // 渐入：两段式去爆音处理
    // - 第1段：0 -> minStartGain 的快速微小渐入（6~15ms），避免瞬时步进引发“爆音”
    // - 第2段：从 minStartGain 到 1 的等功率曲线，保证能量平滑提升
    const fade = this.fadeMs / 1000;
    try {
      const pre = Math.min(0.015, Math.max(0.006, fade * 0.2));
      g.gain.cancelScheduledValues(startAt);
      g.gain.setValueAtTime(0.0001, startAt);
      try {
        g.gain.exponentialRampToValueAtTime(
          Math.max(0.0002, this.minStartGain),
          startAt + pre
        );
      } catch {
        g.gain.linearRampToValueAtTime(
          Math.max(0.0002, this.minStartGain),
          startAt + pre
        );
      }
      const remain = Math.max(0.02, Math.max(0, fade - pre));
      const N = 32;
      const curve = new Float32Array(N);
      for (let i = 0; i < N; i++) {
        const t = (i + 1) / N; // 0..1
        const val = Math.sin((t * Math.PI) / 2); // 0..1 等功率
        curve[i] = this.minStartGain + (1 - this.minStartGain) * val;
      }
      (g.gain as any).setValueCurveAtTime(curve, startAt + pre, remain);
    } catch {}

    // 当前条目的末端：对齐零交叉并预先淡出+停止，避免自然结束的断切
    try {
      const zc = (it as any).__effEndZeroCrossSec;
      let effAudible =
        typeof zc === "number" && isFinite(zc) && zc > 0
          ? zc
          : (it as any).duration || it.buffer.duration;
      effAudible = Math.max(
        0.03,
        Math.min(effAudible, it.buffer.duration - headSkip)
      );
      const endAt = startAt + effAudible;
      const preOut = Math.min(
        0.03,
        Math.max(0.012, Math.max(0.015, effAudible * 0.1))
      );
      try {
        g.gain.setTargetAtTime(
          0.0001,
          endAt - preOut,
          Math.max(0.003, preOut / 3)
        );
      } catch {
        g.gain.linearRampToValueAtTime(0.0001, endAt);
      }
      try {
        src.stop(endAt + 0.008);
      } catch {}
    } catch {}

    // 如果是上一条，安排淡出
    if (index > this.currentIndex) {
      const prevGain = this.sourceGains.get(this.currentIndex);
      const prevSrc = this.activeSources.get(this.currentIndex);
      if (prevGain && prevSrc) {
        // const prevEnd = startAt + fade; // 与新条淡入对齐
        try {
          const now = this.ac.currentTime;
          const t0 = Math.max(now, startAt + this.prevFadeDelaySec);
          if (!this.foreHandoff) {
            // 不取消既有包络，直接使用 target 方式避免“台阶”
            const outDur = Math.max(0.06, fade);
            try {
              prevGain.gain.setTargetAtTime(0.0001, t0, outDur / 3);
            } catch {
              prevGain.gain.linearRampToValueAtTime(0.0001, t0 + outDur);
            }
            prevSrc.stop(t0 + outDur + 0.03);
          }
        } catch {}
      }
    }

    // 使用“可听时长 + 目标间隙”确定下一条触发时间，避免踩到可听尾音
    let effDur =
      (it as any).duration && (it as any).duration > 0
        ? (it as any).duration
        : it.buffer.duration;
    // 若已计算到“有效末端零交叉”，并开启“上一句必须完整”模式，则将边界对齐到该零交叉点
    try {
      const effZc: number | undefined = (it as any).__effEndZeroCrossSec;
      if (
        this.ensurePrevComplete &&
        typeof effZc === "number" &&
        isFinite(effZc) &&
        effZc > 0 &&
        effZc <= effDur
      ) {
        effDur = effZc;
      }
    } catch {}
    const dur = Math.max(0.02, effDur + Math.max(0, this.targetGapSec));
    this.activeSources.set(index, src);
    this.sourceGains.set(index, g);
    try {
      const keys = Array.from(this.activeSources.keys()).join(",");
      const dup =
        Array.from(this.activeSources.keys()).filter(k => k === index).length >
        1;
      console.warn(
        `[probe] WA_SCHEDULE index=${index} dup=${dup} curIdx=${this.currentIndex} playing=${this.isPlaying} activeKeys=[${keys}]`
      );
    } catch {}

    // 更新 nextStartAt 与当前索引
    if (index === this.currentIndex) {
      this.nextStartAt = startAt + dur;
    } else if (index === this.currentIndex + 1) {
      // 保存下一条的预期开始时间，避免时长混淆
      const nextItem = this.playlist[index];
      if (nextItem) {
        (nextItem as any).__scheduledStartAt = startAt;
      }
    }
    // 仅在“当前索引真正开始”时上报 audioStart，避免预排(next)或重复调度导致瞬时多次触发
    if (index === this.currentIndex) {
      (src as any).__audioStartEmitted = true;
      emitter.emit("audioStart", {
        roomId: this.roomId,
        audioId: it.id,
        text: it.text,
        commentId: it.commentId || null,
        player: "seamlessWebAudio"
      });
    } else {
      (src as any).__audioStartEmitted = false;
    }
    // 当该 source 结束时，推进索引并回调
    src.onended = () => {
      if (this.tearingDown || this.destroyed) {
        return;
      }
      // 只有当结束的是当前索引时，推进状态
      if (index === this.currentIndex) {
        // 清理当前播放完成的音频缓存
        const finishedItem = this.playlist[index];
        if (finishedItem && finishedItem.buffer) {
          console.warn(
            `[wa-player] 清理已播放完成的音频缓存，索引: ${index}，ID: ${finishedItem.id}`
          );
          finishedItem.buffer = undefined;
        }

        // 清理底层音频资源
        this.cleanupAudioResources(index);

        this.currentIndex++;
        if (this.onComplete) {
          try {
            this.onComplete(index);
          } catch {}
        }
        const isHidden =
          typeof document !== "undefined" ? document.hidden : false;
        // 🍎 前台交接模式：不在这里安排下一条，交给 Howler 接管
        if (this.foreHandoff && !isHidden) {
          try {
            console.warn(
              `[handoff] WA onended foreHandoff=true visible=true -> no next schedule, idx=${this.currentIndex}`
            );
          } catch {}
          // 明确关闭 WA 播放状态，交由前台 Howler 接管，避免两边都认为在播而不推进
          this.isPlaying = false;
          this.stopProgressTimer();
          // 兜底：若稍后解除 handoff/suppress，则尝试自愈恢复（避免长期句读）
          try {
            const resumeDelay = 300;
            setTimeout(() => {
              try {
                const stillBlocked = this.foreHandoff || this.suppressAutoNext;
                if (!stillBlocked && this.currentIndex < this.playlist.length) {
                  const next = this.playlist[this.currentIndex];
                  if (next && (next as any).buffer) {
                    try {
                      console.warn(
                        `[fix] WA onended unblock resume idx=${this.currentIndex}`
                      );
                    } catch {}
                    this.play();
                  }
                }
              } catch {}
            }, resumeDelay);
          } catch {}
          return;
        }
        // 自动排下一条（若已经解码且存在）
        // 挂起切换：统一阻断 onended 的自动排程（前/后台均有效）
        if (this.suppressAutoNext) {
          try {
            console.warn(
              `[handoff] WA onended suppressAutoNext=true -> no next schedule, idx=${this.currentIndex}`
            );
          } catch {}
          this.isPlaying = false;
          this.stopProgressTimer();
          // 兜底：等待短暂窗口后检查是否已解除，若解除且下一条可播则立即恢复
          try {
            const resumeDelay = 300;
            setTimeout(() => {
              try {
                const stillBlocked = this.suppressAutoNext || this.foreHandoff;
                if (!stillBlocked && this.currentIndex < this.playlist.length) {
                  const next = this.playlist[this.currentIndex];
                  if (next && (next as any).buffer) {
                    try {
                      console.warn(
                        `[fix] WA onended unblock resume idx=${this.currentIndex}`
                      );
                    } catch {}
                    this.play();
                  }
                }
              } catch {}
            }, resumeDelay);
          } catch {}
          return;
        }
        // 仅调度允许产品的条目：跳过不匹配的 productId
        if (this.allowedProductId) {
          while (this.currentIndex < this.playlist.length) {
            const nextAny: any = this.playlist[this.currentIndex];
            const pid = nextAny?.productId || null;
            if (pid && pid !== this.allowedProductId) {
              try {
                console.warn(
                  `[handoff] skip disallowed next idx=${this.currentIndex} pid=${pid}`
                );
              } catch {}
              this.currentIndex++;
              continue;
            }
            break;
          }
        }
        if (this.currentIndex < this.playlist.length) {
          const next = this.playlist[this.currentIndex];
          if (next?.buffer) {
            console.warn(
              `[probe] WA_SCHEDULE_REASON=ONENDED index=${this.currentIndex}`
            );
            this.scheduleIndex(this.currentIndex);
          } else {
            // 🔧 关键修复: 下一个存在但未解码时的处理
            console.warn(
              `[fix] WA_NEXT_BUFFER_PENDING idx=${this.currentIndex} -> 等待解码`
            );

            // 不立即启动 keepAlive,先等待一小段时间
            const DECODE_WAIT_MS = 500; // 等待500ms看是否能解码完成

            setTimeout(() => {
              // 再次检查是否已解码
              const checkNext = this.playlist[this.currentIndex];
              if (checkNext?.buffer) {
                console.warn(
                  `[fix] WA_DECODE_COMPLETED idx=${this.currentIndex} -> 立即调度`
                );
                this.scheduleIndex(this.currentIndex);
                this.startProgressTimer();
              } else {
                // 确实还在解码,启动 keepAlive 并开始轮询
                console.warn(
                  `[fix] WA_START_KEEPALIVE_AND_POLL idx=${this.currentIndex}`
                );
                this.startKeepAlive();
                const expectId = (checkNext as any)?.id;

                const tick = () => {
                  if (this.destroyed || this.tearingDown) return;
                  const n = this.playlist[this.currentIndex] as any;

                  if (n && (!expectId || n.id === expectId) && n.buffer) {
                    console.warn(
                      `[fix] WA_POLL_SUCCESS idx=${this.currentIndex} -> 停止keepAlive并调度`
                    );
                    this.stopKeepAlive();
                    this.scheduleIndex(this.currentIndex);
                    this.startProgressTimer();
                    return;
                  }

                  setTimeout(tick, 50);
                };

                setTimeout(tick, 50);
              }
            }, DECODE_WAIT_MS);
          }
        } else {
          // 🔧 关键修复: 队列为空或新音频还未到达时的处理
          console.warn(`[fix] WA_QUEUE_EMPTY -> 延迟启动keepAlive,等待新音频`);

          // 延迟启动 keepAlive,给新音频一点时间到达
          const EMPTY_WAIT_MS = 300;

          setTimeout(() => {
            // 再次检查队列是否还是空的
            if (
              this.currentIndex >= this.playlist.length &&
              !this.tearingDown &&
              !this.destroyed
            ) {
              console.warn(`[fix] WA_CONFIRMED_EMPTY -> 启动keepAlive保持会话`);
              this.startKeepAlive();
            } else {
              console.warn(`[fix] WA_NEW_AUDIO_ARRIVED -> 无需keepAlive`);
            }
          }, EMPTY_WAIT_MS);
        }
      }
    };

    // 取消链式立即预排：只在 onended 中调度下一条，避免短时间内多源并排
  }

  // 轻量尾部静音检测，返回有效时长（秒）
  private analyzeTailSilence(buf: AudioBuffer): {
    effectiveSec: number;
    silenceSec: number;
  } {
    try {
      const sr = buf.sampleRate || 44100;
      const channels = buf.numberOfChannels;
      const maxLookbackSec = 0.8; // 向后查看最多 800ms 尾部
      const windowSec = 0.01; // 10ms 分析窗
      const stepSec = 0.005; // 5ms 步进，提升分辨率
      const lookback = Math.min(buf.length, Math.floor(sr * maxLookbackSec));
      const win = Math.max(1, Math.floor(sr * windowSec));
      const step = Math.max(1, Math.floor(sr * stepSec));
      const startIndex = buf.length - lookback;
      let silentSamples = 0;
      let i = buf.length - win;
      // 自适应阈值：以 -48dB 为基准，避免尾噪影响（TTS 尾部常见极低噪声）
      const rmsThreshold = 0.004;
      while (i >= startIndex) {
        let maxRms = 0;
        for (let ch = 0; ch < channels; ch++) {
          const data = buf.getChannelData(ch);
          let sum = 0;
          for (let k = 0; k < win; k++) {
            const v = data[i + k] || 0;
            sum += v * v;
          }
          const rms = Math.sqrt(sum / win);
          if (rms > maxRms) maxRms = rms;
        }
        if (maxRms < rmsThreshold) {
          silentSamples += step;
          i -= step;
        } else {
          break;
        }
      }
      const silenceSec = silentSamples / sr;
      const eff = Math.max(0, buf.duration - silenceSec);
      // 不再强制最少静默阈值，全部交给“目标间隙”在调度阶段补偿
      return { effectiveSec: eff, silenceSec };
    } catch {
      return { effectiveSec: buf.duration, silenceSec: 0 };
    }
  }

  // 头/尾静音同时分析：返回 head/tail 与有效时长（去头尾静音）
  private analyzeSilence(buf: AudioBuffer): {
    effectiveSec: number;
    headSilenceSec: number;
    tailSilenceSec: number;
    effEndZeroCrossSec?: number;
  } {
    try {
      const sr = buf.sampleRate || 44100;
      const channels = buf.numberOfChannels;
      const maxLookSec = 1.0; // 最多查看 1s 头尾
      const winSec = 0.01; // 10ms 窗
      const stepSec = 0.005; // 5ms 步
      const win = Math.max(1, Math.floor(sr * winSec));
      const step = Math.max(1, Math.floor(sr * stepSec));
      const look = Math.min(buf.length, Math.floor(sr * maxLookSec));
      const rmsThresholdHead = 0.004; // 头部阈值，约-48dB
      const rmsThresholdTail = 0.0025; // 尾部阈值更严格，约-52dB，保守判别可听尾音

      // 头部静音
      let headSilent = 0;
      let i = 0;
      while (i + win <= Math.min(look, buf.length)) {
        let maxRms = 0;
        for (let ch = 0; ch < channels; ch++) {
          const data = buf.getChannelData(ch);
          let sum = 0;
          for (let k = 0; k < win; k++) {
            const v = data[i + k] || 0;
            sum += v * v;
          }
          const rms = Math.sqrt(sum / win);
          if (rms > maxRms) maxRms = rms;
        }
        if (maxRms < rmsThresholdHead) {
          headSilent += step;
          i += step;
        } else {
          break;
        }
      }

      // 尾部静音
      let tailSilent = 0;
      let j = buf.length - win;
      const tailStart = buf.length - look;
      while (j >= Math.max(0, tailStart)) {
        let maxRms = 0;
        for (let ch = 0; ch < channels; ch++) {
          const data = buf.getChannelData(ch);
          let sum = 0;
          for (let k = 0; k < win; k++) {
            const v = data[j + k] || 0;
            sum += v * v;
          }
          const rms = Math.sqrt(sum / win);
          if (rms > maxRms) maxRms = rms;
        }
        if (maxRms < rmsThresholdTail) {
          tailSilent += step;
          j -= step;
        } else {
          break;
        }
      }

      const headSec = headSilent / sr;
      const tailSec = tailSilent / sr;
      const eff = Math.max(0, buf.duration - headSec - tailSec);

      // 在有效区末尾附近（<=10ms）寻找最接近零交叉的样本，用于边界对齐
      let effEndZeroCrossSec: number | undefined = undefined;
      try {
        const searchWinSec = 0.02; // 扩大到 20ms，提升零交叉命中率
        const winSamples = Math.max(
          1,
          Math.floor(sr * Math.min(searchWinSec, Math.max(0, eff)))
        );
        const effEndIndex = Math.min(
          buf.length,
          Math.floor((headSec + eff) * sr)
        );
        const effStartIndex = Math.max(
          Math.floor(headSec * sr),
          effEndIndex - winSamples
        );
        let bestIdx = effEndIndex - 1;
        let bestAbs = 1e9;
        for (let ch = 0; ch < channels; ch++) {
          const data = buf.getChannelData(ch);
          for (let i = effEndIndex - 1; i >= effStartIndex; i--) {
            const v = Math.abs(data[i] || 0);
            if (v < bestAbs) {
              bestAbs = v;
              bestIdx = i;
            }
          }
        }
        const zcOffsetSamples = effEndIndex - bestIdx; // >=1
        effEndZeroCrossSec = Math.max(0, eff - zcOffsetSamples / sr);
      } catch {}

      return {
        effectiveSec: eff,
        headSilenceSec: headSec,
        tailSilenceSec: tailSec,
        effEndZeroCrossSec
      };
    } catch {
      return {
        effectiveSec: buf.duration,
        headSilenceSec: 0,
        tailSilenceSec: 0
      };
    }
  }

  // 绑定唯一 <audio> 元素输出（媒体会话在该元素上维护）
  attachToElement(el: HTMLAudioElement) {
    try {
      if (!this.dest) {
        this.dest = this.ac.createMediaStreamDestination();
      }
      // 重新路由：断开旧连接，通过 freezeGuard（若存在）接到 dest
      if (this.freezeGuard) {
        try {
          this.freezeGuard.disconnect();
        } catch {}
        this.freezeGuard.connect(this.dest);
      } else {
        try {
          this.gain.disconnect();
        } catch {}
        this.gain.connect(this.dest);
      }
      (el as any).srcObject = (this.dest as MediaStreamAudioDestinationNode)
        .stream as any;
      this.attachedAudio = el;
    } catch {}
  }

  private async fetchAndDecode(url: string): Promise<AudioBuffer> {
    const res = await fetch(url, { cache: "no-store" });
    const ab = await res.arrayBuffer();
    return await new Promise<AudioBuffer>((resolve, reject) => {
      this.ac.decodeAudioData(
        ab,
        buf => resolve(buf),
        err => reject(err)
      );
    });
  }

  private startProgressTimer() {
    this.stopProgressTimer();
    const step = () => {
      if (!this.isPlaying) return;
      const cur = this.playlist[this.currentIndex];
      if (!cur?.buffer) {
        this.progressTimer = window.requestAnimationFrame(
          step
        ) as unknown as number;
        return;
      }
      // 使用“有效时长”推导当前曲目的开始时间与进度；未调度时进度为 0，避免切后台瞬间误判
      const eff = (cur as any).duration || cur.buffer.duration;
      let p = 0;
      if (eff > 0 && this.nextStartAt && this.nextStartAt > 0) {
        const curStart = this.nextStartAt - eff;
        const t = Math.max(0, this.ac.currentTime - curStart);
        p = Math.min(1, Math.max(0, t / eff));
      } else {
        p = 0;
      }
      if (this.onProgress) {
        try {
          this.onProgress(this.currentIndex, p);
        } catch {}
      }
      this.progressTimer = window.requestAnimationFrame(
        step
      ) as unknown as number;
    };
    this.progressTimer = window.requestAnimationFrame(
      step
    ) as unknown as number;
  }

  private stopProgressTimer() {
    if (this.progressTimer) {
      try {
        cancelAnimationFrame(this.progressTimer as unknown as number);
      } catch {}
      this.progressTimer = undefined;
    }
  }

  private startKeepAlive() {
    if (this.keepAliveActive) return;
    // 创建 1s 静音缓冲，并循环播放，保持音频会话与后台活跃
    const buf = this.ac.createBuffer(1, this.ac.sampleRate, this.ac.sampleRate);
    const src = this.ac.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    const g = this.ac.createGain();
    g.gain.value = 0.0001; // 几乎无声
    src.connect(g);
    g.connect(this.gain);
    try {
      src.start(this.ac.currentTime + 0.01);
    } catch {}
    this.keepAliveSrc = src;
    this.keepAliveGain = g;
    this.keepAliveActive = true;
    this.isPlaying = true; // 视为仍在播放，保持进度与会话
  }

  private stopKeepAlive() {
    if (!this.keepAliveActive) return;
    console.warn(`[fix] stopKeepAlive 被调用`);
    try {
      this.keepAliveSrc?.stop();
    } catch {}
    this.keepAliveSrc = undefined;
    this.keepAliveGain = undefined;
    this.keepAliveActive = false;
    // 🔧 关键修复: 不要立即设置 isPlaying=false
    // 让调用者决定是否继续播放
    console.warn(
      `[fix] stopKeepAlive 完成, isPlaying保持=${this.isPlaying}, 由调用者决定后续状态`
    );
    // 如果没有实际播放内容，则整体视为未播放
    // if (this.currentIndex >= this.playlist.length) {
    //   this.isPlaying = false;
    //   this.stopProgressTimer();
    // }
  }

  // 在边界切换时，如当前 index 条目不属于允许产品，立即淡出并停止，移除该条目
  stopCurrentIfDisallowed(allowedPid: string) {
    try {
      if (!allowedPid) return;
      const it: any = this.playlist[this.currentIndex];
      const pid = it?.productId || null;
      if (!it || pid === allowedPid) return;
      // 淡出当前源并停止
      const src = this.activeSources.get(this.currentIndex);
      const g = this.sourceGains.get(this.currentIndex);
      const now = this.ac.currentTime;
      try {
        if (g && g.gain) {
          g.gain.cancelScheduledValues(now);
          g.gain.setValueAtTime(g.gain.value, now);
          g.gain.linearRampToValueAtTime(0.0001, now + 0.06);
        }
      } catch {}
      try {
        if (src) src.stop(now + 0.08);
      } catch {}
      try {
        this.activeSources.delete(this.currentIndex);
      } catch {}
      try {
        this.sourceGains.delete(this.currentIndex);
      } catch {}
      // 从播放列表移除该条（当前 index），不要推进 currentIndex，这样下一条自然成为当前
      try {
        this.playlist.splice(this.currentIndex, 1);
      } catch {}
      // 重新设置 nextStartAt 使下一条可以尽快安排
      try {
        this.nextStartAt = Math.max(
          this.ac.currentTime + 0.02,
          this.ac.currentTime
        );
      } catch {}
      try {
        console.warn(
          `[fix] WA_STOP_DISALLOWED_CURRENT idx=${this.currentIndex} pid=${pid} allowed=${allowedPid}`
        );
      } catch {}
    } catch {}
  }

  // 冻结当前源的增益曲线一小段时间（用于切换窗口内抖动保护）
  freezeCurrentGain(ms: number) {
    try {
      const g = this.sourceGains.get(this.currentIndex);
      if (!g || !g.gain) return;
      const now = this.ac.currentTime;
      const cur = (() => {
        try {
          return g.gain.value;
        } catch {
          return 1.0;
        }
      })();
      // 保持当前电平：设定为当前值，并在hold末端再确认一次，尽量不打断后续已排程的包络
      try {
        g.gain.setValueAtTime(cur, now);
      } catch {}
      try {
        g.gain.setValueAtTime(cur, now + Math.max(0, ms));
      } catch {}
    } catch {}
  }

  // 在冻结窗口内对最终输出做一个极短、低幅度的衰减脉冲，进一步压小可闻重音
  pulseFreezeGuard(totalMs?: number, depth?: number) {
    try {
      if (!this.freezeGuard) return;
      const now = this.ac.currentTime;
      // 基于当前输出能量自适应深度与时长
      let rms = 0.0;
      try {
        if (this.analyser && this.anaBuf) {
          this.analyser.getFloatTimeDomainData(this.anaBuf);
          let sum = 0;
          for (let i = 0; i < this.anaBuf.length; i++) {
            const v = this.anaBuf[i];
            sum += v * v;
          }
          rms = Math.sqrt(sum / this.anaBuf.length);
        }
      } catch {}
      const useTotal =
        typeof totalMs === "number" ? totalMs : rms > 0.2 ? 120 : 80;
      const useDepth =
        typeof depth === "number" ? depth : rms > 0.2 ? 0.8 : 0.85;
      const inDur = Math.min(0.02, Math.max(0.005, (useTotal / 1000) * 0.25));
      const holdDur = Math.max(0, useTotal / 1000 - inDur * 2);
      const outDur = inDur * 1.2;
      const g = this.freezeGuard.gain;
      const cur = (() => {
        try {
          return g.value;
        } catch {
          return 1.0;
        }
      })();
      try {
        g.cancelScheduledValues(now);
      } catch {}
      try {
        g.setValueAtTime(cur, now);
      } catch {}
      const target = Math.max(0.5, Math.min(1.0, useDepth));
      try {
        g.linearRampToValueAtTime(target, now + inDur);
      } catch {}
      try {
        g.setValueAtTime(target, now + inDur + holdDur);
      } catch {}
      try {
        g.linearRampToValueAtTime(1.0, now + inDur + holdDur + outDur);
      } catch {}
    } catch {}
  }

  // 清理底层音频资源
  private cleanupAudioResources(index: number) {
    try {
      // 清理activeSources中的AudioBufferSourceNode
      const src = this.activeSources.get(index);
      if (src) {
        console.warn(`[wa-player] 清理底层音频资源，索引: ${index}`);
        try {
          // 停止并断开连接
          src.stop();
          src.disconnect();
        } catch {
          // 忽略已停止或已断开的情况
        }
        this.activeSources.delete(index);
      }

      // 清理sourceGains中的GainNode
      const gain = this.sourceGains.get(index);
      if (gain) {
        try {
          gain.disconnect();
        } catch {
          // 忽略已断开的情况
        }
        this.sourceGains.delete(index);
      }

      console.warn(`[wa-player] 底层音频资源清理完成，索引: ${index}`);
    } catch (error) {
      console.error(
        `[wa-player] 清理底层音频资源失败，索引: ${index}，错误:`,
        error
      );
    }
  }

  // 切换瞬间临时提升压缩器强度，窗口后还原
  boostCompressor(ms: number, strong: boolean = false) {
    try {
      if (!this.comp) return;
      // 记录当前设置
      const cur = {
        t: this.comp.threshold.value,
        r: this.comp.ratio.value,
        k: this.comp.knee.value,
        a: this.comp.attack.value,
        rel: this.comp.release.value
      };
      // 强压设置（更深/更快），仅在窗口内使用
      try {
        // 回退 strong 分支到旧值，并恢复统一的 knee/attack/release
        this.comp.threshold.value = strong ? -18 : -12;
        this.comp.ratio.value = strong ? 12 : 8;
        this.comp.knee.value = 30;
        this.comp.attack.value = 0.0015;
        this.comp.release.value = 0.06;
      } catch {}
      window.setTimeout(
        () => {
          try {
            if (!this.comp) return;
            this.comp.threshold.value = cur.t;
            this.comp.ratio.value = cur.r;
            this.comp.knee.value = cur.k;
            this.comp.attack.value = cur.a;
            this.comp.release.value = cur.rel;
          } catch {}
        },
        Math.max(0, Math.floor(ms))
      );
    } catch {}
  }
}
